import { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { API } from "../App";
import { Trophy, Star, Zap, BookOpen, Award, Heart, Target, Loader, Gift, CheckCircle, Circle, Sparkles, Share2, Download } from "lucide-react";
import { AvatarDisplay } from "./ParentDashboard";
import { BottomNav } from "./ChildDashboard";
import { useLanguage } from "../hooks/useLanguage";
import { ShareButton, QuickShareButton } from "./SocialShare";

const BADGE_CONFIG = {
  first_lesson: { icon: BookOpen, grad: ["#118AB2","#4CC9F0"] },
  week_warrior: { icon: Zap,      grad: ["#9B5DE5","#4CC9F0"] },
  star_student: { icon: Star,     grad: ["#FFD166","#F77F00"] },
  task_master:  { icon: Trophy,   grad: ["#06D6A0","#118AB2"] },
  faithful_30:  { icon: Heart,    grad: ["#EF476F","#FFD166"] },
  month_streak: { icon: Target,   grad: ["#F77F00","#FFD166"] },
};

// Flying Bird Component
const FlyingBird = ({ delay, startX, color }) => (
  <motion.svg
    viewBox="0 0 24 24"
    className="absolute w-6 h-6 sm:w-8 sm:h-8 pointer-events-none"
    style={{ color }}
    initial={{ x: startX, y: "100vh", opacity: 0, rotate: -10 }}
    animate={{
      x: [startX, startX + 100, startX + 50, startX + 150],
      y: ["100vh", "60vh", "30vh", "-10vh"],
      opacity: [0, 1, 1, 0],
      rotate: [-10, 10, -5, 15],
    }}
    transition={{
      duration: 6,
      delay,
      repeat: Infinity,
      repeatDelay: Math.random() * 4 + 2,
      ease: "easeInOut",
    }}
  >
    <path
      fill="currentColor"
      d="M21.8,13.1c-0.2-0.3-0.5-0.5-0.8-0.5c-0.3,0-0.5,0.1-0.7,0.3l-2.3,2.5l-3.2-2.4c-0.4-0.3-0.9-0.3-1.3,0l-3.2,2.4 l-2.3-2.5c-0.2-0.2-0.4-0.3-0.7-0.3c-0.3,0-0.6,0.2-0.8,0.5c-0.3,0.5-0.2,1.1,0.2,1.5l3,3.3c0.2,0.2,0.5,0.3,0.8,0.3 c0.3,0,0.5-0.1,0.7-0.3l2.6-2l2.6,2c0.2,0.2,0.4,0.3,0.7,0.3c0.3,0,0.6-0.1,0.8-0.3l3-3.3C22,14.2,22.1,13.6,21.8,13.1z"
    />
    <path
      fill="currentColor"
      d="M12,6c-0.6,0-1,0.4-1,1v4c0,0.6,0.4,1,1,1s1-0.4,1-1V7C13,6.4,12.6,6,12,6z"
    />
  </motion.svg>
);

// Twinkling Star Component
const TwinklingStar = ({ size, top, left, delay, color }) => (
  <motion.div
    className="absolute pointer-events-none"
    style={{ top, left }}
    initial={{ scale: 0, opacity: 0, rotate: 0 }}
    animate={{
      scale: [0, 1.2, 0.8, 1],
      opacity: [0, 1, 0.7, 1],
      rotate: [0, 180, 360],
    }}
    transition={{
      duration: 2,
      delay,
      repeat: Infinity,
      repeatType: "reverse",
      ease: "easeInOut",
    }}
  >
    <Star size={size} fill={color} color={color} strokeWidth={0} />
  </motion.div>
);

// Big Floating Star Component
const BigFloatingStar = ({ delay, startY }) => (
  <motion.div
    className="absolute pointer-events-none"
    initial={{ x: "110vw", y: startY, scale: 0.5, opacity: 0 }}
    animate={{
      x: ["-10vw"],
      y: [startY, `${parseInt(startY) - 20}vh`, startY],
      scale: [0.5, 1.5, 1, 1.3, 0.8],
      opacity: [0, 0.9, 1, 0.8, 0],
      rotate: [0, 360],
    }}
    transition={{
      duration: 8,
      delay,
      repeat: Infinity,
      repeatDelay: Math.random() * 5 + 3,
      ease: "linear",
    }}
  >
    <Star size={48} fill="#FFD166" color="#F77F00" strokeWidth={2} className="drop-shadow-lg" />
  </motion.div>
);

// Sparkle burst for celebrations
const SparkleBurst = ({ show }) => {
  const sparkles = useMemo(() => 
    Array.from({ length: 12 }, (_, i) => ({
      angle: (i * 30) * (Math.PI / 180),
      distance: 60 + Math.random() * 40,
      size: 8 + Math.random() * 8,
      delay: Math.random() * 0.3,
    })), []
  );

  if (!show) return null;

  return (
    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
      {sparkles.map((s, i) => (
        <motion.div
          key={i}
          className="absolute"
          initial={{ scale: 0, x: 0, y: 0, opacity: 1 }}
          animate={{
            scale: [0, 1.5, 0],
            x: Math.cos(s.angle) * s.distance,
            y: Math.sin(s.angle) * s.distance,
            opacity: [1, 1, 0],
          }}
          transition={{ duration: 0.8, delay: s.delay, ease: "easeOut" }}
        >
          <Sparkles size={s.size} color="#FFD166" fill="#FFD166" />
        </motion.div>
      ))}
    </div>
  );
};

// Confetti piece
const ConfettiPiece = ({ delay, color, startX }) => (
  <motion.div
    className="absolute w-3 h-3 rounded-sm pointer-events-none"
    style={{ backgroundColor: color, left: startX }}
    initial={{ y: -20, opacity: 1, rotate: 0 }}
    animate={{
      y: "100vh",
      opacity: [1, 1, 0],
      rotate: [0, 360, 720],
      x: [0, Math.random() * 100 - 50, Math.random() * 100 - 50],
    }}
    transition={{
      duration: 4,
      delay,
      repeat: Infinity,
      repeatDelay: 6,
      ease: "easeIn",
    }}
  />
);

export default function RewardsPage() {
  const { childId } = useParams();
  const { t, language } = useLanguage();
  const [child, setChild] = useState(null);
  const [progress, setProgress] = useState(null);
  const [weeklyPrize, setWeeklyPrize] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrateBadge, setCelebrateBadge] = useState(null);
  const [certData, setCertData] = useState(null);
  const [certificates, setCertificates] = useState([]);
  const [showCertModal, setShowCertModal] = useState(null);

  const ALL_BADGES = [
    { type: "first_lesson", name: t('badge_first_steps'),    description: t('badge_first_steps_desc')   },
    { type: "week_warrior", name: t('badge_week_warrior'),     description: t('badge_week_warrior_desc')  },
    { type: "star_student", name: t('badge_star_student'),     description: t('badge_star_student_desc')        },
    { type: "task_master",  name: t('badge_task_master'),      description: t('badge_task_master_desc')      },
    { type: "faithful_30",  name: t('badge_faithful_friend'),  description: t('badge_faithful_friend_desc')        },
    { type: "month_streak", name: t('badge_month_champion'),   description: t('badge_month_champion_desc')               },
  ];

  // Generate random birds and stars
  const flyingBirds = useMemo(() => 
    Array.from({ length: 5 }, (_, i) => ({
      id: i,
      delay: i * 1.5,
      startX: `${Math.random() * 80}vw`,
      color: ["#118AB2", "#06D6A0", "#9B5DE5", "#EF476F", "#FFD166"][i % 5],
    })), []
  );

  const twinklingStars = useMemo(() =>
    Array.from({ length: 15 }, (_, i) => ({
      id: i,
      size: 12 + Math.random() * 16,
      top: `${10 + Math.random() * 80}%`,
      left: `${5 + Math.random() * 90}%`,
      delay: Math.random() * 3,
      color: ["#FFD166", "#F77F00", "#EF476F", "#9B5DE5", "#06D6A0"][i % 5],
    })), []
  );

  const bigStars = useMemo(() =>
    Array.from({ length: 3 }, (_, i) => ({
      id: i,
      delay: i * 4 + 2,
      startY: `${20 + i * 25}vh`,
    })), []
  );

  const confettiColors = ["#FFD166", "#EF476F", "#06D6A0", "#9B5DE5", "#118AB2", "#F77F00"];
  const confettiPieces = useMemo(() =>
    Array.from({ length: 20 }, (_, i) => ({
      id: i,
      delay: Math.random() * 2,
      color: confettiColors[i % confettiColors.length],
      startX: `${Math.random() * 100}%`,
    })), []
  );

  useEffect(() => { loadData(); }, [childId]);

  const loadData = async () => {
    try {
      const [cr, pr, wp, certCheck, certList] = await Promise.all([
        axios.get(`${API}/children/${childId}`),
        axios.get(`${API}/progress/${childId}`),
        axios.get(`${API}/children/${childId}/weekly-prize`),
        axios.get(`${API}/achievements/certificates/check/${childId}`).catch(() => ({ data: null })),
        axios.get(`${API}/achievements/certificates/${childId}`).catch(() => ({ data: [] })),
      ]);
      setChild(cr.data);
      setProgress(pr.data);
      setWeeklyPrize(wp.data);
      setCertData(certCheck.data);
      setCertificates(certList.data || []);
      
      // Show celebration if prize just earned
      const prizeData = wp.data;
      const isEarned = prizeData?.prize?.earned || 
        (prizeData?.week_stats?.lessons_completed >= 5 && prizeData?.week_stats?.tasks_completed >= 15);
      if (isEarned && prizeData?.prize && !localStorage.getItem(`prize_celebrated_${childId}_${prizeData.week_start}`)) {
        setShowCelebration(true);
        localStorage.setItem(`prize_celebrated_${childId}_${prizeData.week_start}`, 'true');
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center pb-20" style={{ background: "#F0F4FF" }}>
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
        >
          <Star size={64} color="#FFD166" fill="#FFD166" />
        </motion.div>
      </motion.div>
    </div>
  );

  const earnedTypes = new Set(progress?.badges?.map(b => b.badge_type) || []);
  const prizeProgress = weeklyPrize?.week_stats || weeklyPrize?.progress;
  const lessonPercent = prizeProgress ? Math.min(100, (prizeProgress.lessons_completed / 5) * 100) : 0;
  const taskPercent = prizeProgress ? Math.min(100, (prizeProgress.tasks_completed / 15) * 100) : 0;

  return (
    <div className="min-h-screen pb-24 relative overflow-hidden" style={{ background: "#F0F4FF" }}>
      {/* Animated Background Elements */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        {/* Flying Birds */}
        {flyingBirds.map((bird) => (
          <FlyingBird key={bird.id} {...bird} />
        ))}
        
        {/* Twinkling Stars */}
        {twinklingStars.map((star) => (
          <TwinklingStar key={star.id} {...star} />
        ))}
        
        {/* Big Floating Stars */}
        {bigStars.map((star) => (
          <BigFloatingStar key={star.id} {...star} />
        ))}
        
        {/* Confetti (always subtle in background) */}
        {confettiPieces.map((piece) => (
          <ConfettiPiece key={piece.id} {...piece} />
        ))}
      </div>

      {/* Celebration Modal */}
      <AnimatePresence>
        {showCelebration && weeklyPrize?.prize && (
          <motion.div 
            className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div 
              className="bg-white rounded-3xl p-6 max-w-sm w-full text-center shadow-2xl relative overflow-hidden"
              initial={{ scale: 0.5, opacity: 0, y: 50 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.5, opacity: 0, y: 50 }}
              transition={{ type: "spring", damping: 15 }}
            >
              <SparkleBurst show={true} />
              <motion.div 
                className="w-24 h-24 rounded-full mx-auto mb-4 flex items-center justify-center relative"
                style={{ background: "linear-gradient(135deg, #FFD166, #F77F00)" }}
                animate={{ scale: [1, 1.1, 1], rotate: [0, 5, -5, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <Gift size={48} color="white" />
                {/* Orbiting stars */}
                {[0, 1, 2].map((i) => (
                  <motion.div
                    key={i}
                    className="absolute"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 3, repeat: Infinity, ease: "linear", delay: i * 1 }}
                    style={{ transformOrigin: "50px 50px" }}
                  >
                    <Star size={16} fill="#FFD166" color="#F77F00" className="absolute -top-8" />
                  </motion.div>
                ))}
              </motion.div>
              <motion.h2 
                className="text-3xl font-black text-[#073B4C] mb-2" 
                style={{ fontFamily: "Fredoka, sans-serif" }}
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 1 }}
              >
                🎉 {t('you_did_it')} 🎉
              </motion.h2>
              <p className="text-gray-600 mb-4">{t('earned_weekly_prize')}</p>
              <motion.div 
                className="bg-gradient-to-r from-[#FFD166] to-[#F77F00] rounded-2xl p-4 mb-4"
                animate={{ boxShadow: ["0 0 20px rgba(255,209,102,0.5)", "0 0 40px rgba(255,209,102,0.8)", "0 0 20px rgba(255,209,102,0.5)"] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <p className="text-white font-black text-xl" style={{ fontFamily: "Fredoka, sans-serif" }}>
                  {weeklyPrize.prize.prize_name}
                </p>
                {weeklyPrize.prize.prize_description && (
                  <p className="text-white/80 text-sm mt-1">{weeklyPrize.prize.prize_description}</p>
                )}
              </motion.div>
              <motion.button
                onClick={() => setShowCelebration(false)}
                className="w-full bg-[#06D6A0] text-white font-black py-3 rounded-xl transition-all"
                style={{ fontFamily: "Fredoka, sans-serif" }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                data-testid="close-celebration-btn"
              >
                {t('awesome')}
              </motion.button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <motion.div 
        className="px-4 pt-6 pb-12 relative z-10"
        style={{ background: "linear-gradient(135deg, #EF476F 0%, #FFD166 100%)" }}
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        <div className="max-w-lg mx-auto flex items-center gap-3 mb-6">
          {child && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", delay: 0.2 }}
            >
              <AvatarDisplay avatar={child.avatar} size={44} />
            </motion.div>
          )}
          <motion.div
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <p className="text-white/70 text-xs font-medium">{t('rewards_for')}</p>
            <h1 className="text-2xl font-black text-white" style={{ fontFamily: "Fredoka, sans-serif" }}>
              {child?.name}
            </h1>
          </motion.div>
        </div>
        {/* Stat pills */}
        <div className="max-w-lg mx-auto grid grid-cols-3 gap-3">
          {[
            { label: t('streak'),  value: `${progress?.current_streak||0}d`, icon: Zap   },
            { label: t('stars'),   value: progress?.total_stars||0,           icon: Star  },
            { label: t('badges'),  value: progress?.badges?.length||0,        icon: Award },
          ].map((s,i) => (
            <motion.div 
              key={i} 
              className="bg-white/25 rounded-2xl p-3 text-center relative overflow-hidden"
              data-testid={`reward-stat-${i}`}
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4 + i * 0.1 }}
              whileHover={{ scale: 1.05, backgroundColor: "rgba(255,255,255,0.35)" }}
            >
              <motion.div
                animate={{ rotate: i === 1 ? [0, 360] : 0 }}
                transition={{ duration: 4, repeat: i === 1 ? Infinity : 0, ease: "linear" }}
              >
                <s.icon size={20} color="white" className="mx-auto mb-1" strokeWidth={2.5} />
              </motion.div>
              <motion.p 
                className="text-white font-black text-2xl"
                key={s.value}
                initial={{ scale: 1.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
              >
                {s.value}
              </motion.p>
              <p className="text-white/80 text-xs font-bold">{s.label}</p>
            </motion.div>
          ))}
        </div>
        
        {/* Share Streak Button */}
        {progress?.current_streak >= 3 && (
          <motion.div 
            className="max-w-lg mx-auto mt-4"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.7 }}
          >
            <ShareButton
              type="streak"
              data={{
                childName: child?.name,
                days: progress?.current_streak,
              }}
              className="w-full bg-white/20 text-white border-2 border-white/30 hover:bg-white/30"
              buttonText={`Share ${progress?.current_streak} Day Streak!`}
              size="default"
            />
          </motion.div>
        )}
      </motion.div>

      <div className="max-w-lg mx-auto px-4 -mt-6 relative z-10">
        {/* Weekly Prize Card */}
        {weeklyPrize?.prize && (
          <motion.div 
            className={`rounded-3xl p-5 shadow-xl mb-5 border-b-4 ${
              prizeProgress?.earned 
                ? 'bg-gradient-to-br from-[#FFD166]/20 to-[#F77F00]/20' 
                : 'bg-white'
            }`} 
            style={{ borderColor: prizeProgress?.earned ? "#F77F00" : "#FFD166" }}
            data-testid="weekly-prize-card"
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5 }}
            whileHover={{ scale: 1.02 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <motion.div 
                className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  prizeProgress?.earned 
                    ? 'bg-gradient-to-br from-[#FFD166] to-[#F77F00]' 
                    : 'bg-[#FFD166]/20'
                }`}
                animate={prizeProgress?.earned ? { rotate: [0, 10, -10, 0] } : {}}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <Gift size={24} color={prizeProgress?.earned ? "white" : "#F77F00"} />
              </motion.div>
              <div className="flex-1">
                <h3 className="font-black text-[#073B4C]" style={{ fontFamily: "Fredoka, sans-serif" }}>
                  {t('this_weeks_prize')}
                </h3>
                <p className="text-sm font-bold text-[#F77F00]">{weeklyPrize.prize.prize_name}</p>
              </div>
              {prizeProgress?.earned && (
                <motion.div 
                  className="bg-[#06D6A0] text-white text-xs font-black px-3 py-1 rounded-full flex items-center gap-1"
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 1, repeat: Infinity }}
                >
                  <Sparkles size={12} /> {t('earned')}
                </motion.div>
              )}
            </div>
            
            {weeklyPrize.prize.prize_description && (
              <p className="text-gray-500 text-sm mb-4 italic">"{weeklyPrize.prize.prize_description}"</p>
            )}
            
            {!prizeProgress?.earned && (
              <div className="space-y-3">
                {/* Lessons progress */}
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="font-bold text-gray-600 flex items-center gap-1">
                      <BookOpen size={12} /> {t('lesson')}
                    </span>
                    <span className="font-bold text-[#118AB2]">
                      {prizeProgress?.lessons_completed || 0}/5
                    </span>
                  </div>
                  <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                    <motion.div 
                      className="h-full rounded-full"
                      style={{ background: "linear-gradient(90deg, #118AB2, #06D6A0)" }}
                      initial={{ width: 0 }}
                      animate={{ width: `${lessonPercent}%` }}
                      transition={{ duration: 1, delay: 0.8 }}
                    />
                  </div>
                </div>
                
                {/* Tasks progress */}
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="font-bold text-gray-600 flex items-center gap-1">
                      <CheckCircle size={12} /> {t('activities')}
                    </span>
                    <span className="font-bold text-[#9B5DE5]">
                      {prizeProgress?.tasks_completed || 0}/15
                    </span>
                  </div>
                  <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                    <motion.div 
                      className="h-full rounded-full"
                      style={{ background: "linear-gradient(90deg, #9B5DE5, #EF476F)" }}
                      initial={{ width: 0 }}
                      animate={{ width: `${taskPercent}%` }}
                      transition={{ duration: 1, delay: 1 }}
                    />
                  </div>
                </div>
                
                <p className="text-xs text-gray-400 text-center mt-2">
                  {t('complete_to_earn')}
                </p>
              </div>
            )}
          </motion.div>
        )}
        
        {/* No Prize Set Message */}
        {!weeklyPrize?.prize && (
          <motion.div 
            className="bg-gray-50 rounded-3xl p-5 mb-5 text-center"
            data-testid="no-prize-card"
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <Gift size={32} color="#D1D5DB" className="mx-auto mb-2" />
            <p className="text-gray-400 font-medium text-sm">
              {t('ask_parent_prize')}
            </p>
          </motion.div>
        )}

        {/* Weekly Certificate Section */}
        <motion.div 
          className="bg-white rounded-3xl p-5 shadow-xl mb-5 border-b-4"
          style={{ borderColor: "#9B5DE5" }}
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.55 }}
          data-testid="rewards-certificate-section"
        >
          <div className="flex items-center gap-3 mb-4">
            <motion.div
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ background: certData?.certificate_earned ? "linear-gradient(135deg, #9B5DE5, #EF476F)" : "#9B5DE520" }}
              animate={certData?.certificate_earned ? { rotate: [0, 10, -10, 0] } : {}}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <Award size={24} color={certData?.certificate_earned ? "white" : "#9B5DE5"} />
            </motion.div>
            <div className="flex-1">
              <h3 className="font-black text-[#073B4C]" style={{ fontFamily: "Fredoka, sans-serif" }}>
                Weekly Certificate
              </h3>
              {certData?.certificate_earned ? (
                <p className="text-sm font-bold text-[#06D6A0]">Earned this week!</p>
              ) : (
                <p className="text-sm font-bold text-[#9B5DE5]">Keep going!</p>
              )}
            </div>
            {certData?.certificate_earned && (
              <motion.span
                className="text-white text-xs font-black px-3 py-1 rounded-full"
                style={{ background: "linear-gradient(90deg, #9B5DE5, #EF476F)" }}
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 1, repeat: Infinity }}
              >
                Earned!
              </motion.span>
            )}
          </div>

          {certData && !certData.certificate_earned && (
            <div className="space-y-2 mb-3">
              <div className="flex items-center gap-2">
                <BookOpen size={14} color="#118AB2" />
                <div className="flex-1 h-2.5 bg-gray-100 rounded-full overflow-hidden">
                  <motion.div className="h-full rounded-full"
                    style={{ background: "linear-gradient(90deg, #118AB2, #06D6A0)" }}
                    initial={{ width: 0 }}
                    animate={{ width: `${Math.min(100, ((certData.lessons_completed || 0) / (certData.lessons_required || 7)) * 100)}%` }}
                    transition={{ duration: 1, delay: 0.6 }} />
                </div>
                <span className="text-xs font-bold text-gray-500 min-w-[55px] text-right">
                  {certData.lessons_completed}/{certData.lessons_required} lessons
                </span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle size={14} color="#9B5DE5" />
                <div className="flex-1 h-2.5 bg-gray-100 rounded-full overflow-hidden">
                  <motion.div className="h-full rounded-full"
                    style={{ background: "linear-gradient(90deg, #9B5DE5, #EF476F)" }}
                    initial={{ width: 0 }}
                    animate={{ width: `${Math.min(100, ((certData.tasks_completed || 0) / (certData.tasks_required || 15)) * 100)}%` }}
                    transition={{ duration: 1, delay: 0.7 }} />
                </div>
                <span className="text-xs font-bold text-gray-500 min-w-[55px] text-right">
                  {certData.tasks_completed || 0}/{certData.tasks_required || 15} activities
                </span>
              </div>
              <p className="text-xs text-center text-purple-400 font-bold mt-2">
                Complete all lessons & activities to earn your certificate and bring it to family lesson!
              </p>
            </div>
          )}

          {certData?.certificate_earned && certData.certificate && (
            <div className="space-y-3">
              <div className="border-2 border-[#9B5DE5] rounded-2xl p-4 text-center bg-purple-50/50">
                <div className="flex justify-center gap-1 mb-2">
                  {[...Array(5)].map((_, i) => <Star key={i} size={16} color="#FFD166" fill="#FFD166" />)}
                </div>
                <p className="text-lg font-black text-[#9B5DE5]" style={{ fontFamily: "Fredoka, sans-serif" }}>
                  Week {certData.certificate.week_number}: {certData.certificate.theme}
                </p>
                <p className="text-xs text-gray-500 italic mt-1">{certData.certificate.scripture}</p>
              </div>
              <button
                onClick={() => setShowCertModal(certData.certificate)}
                className="w-full py-3 rounded-xl font-bold text-white flex items-center justify-center gap-2 hover:scale-[1.02] active:scale-[0.98] transition-all"
                style={{ background: "linear-gradient(90deg, #9B5DE5, #EF476F)" }}
                data-testid="print-certificate-rewards-btn"
              >
                <Download size={16} />
                Print Certificate for Family Lesson
              </button>
            </div>
          )}

          {/* Past certificates */}
          {certificates.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2">Past Certificates</p>
              <div className="space-y-2">
                {certificates.map(cert => (
                  <button key={cert.id}
                    onClick={() => setShowCertModal(cert)}
                    className="w-full flex items-center gap-3 bg-yellow-50 rounded-xl p-2.5 border border-yellow-100 hover:bg-yellow-100 transition-all text-left"
                    data-testid={`past-cert-${cert.week_number}`}
                  >
                    <Award size={16} color="#F77F00" />
                    <div className="flex-1">
                      <p className="text-sm font-bold text-gray-800">Week {cert.week_number}: {cert.theme}</p>
                      <p className="text-xs text-gray-500">{new Date(cert.earned_at).toLocaleDateString()}</p>
                    </div>
                    <Download size={14} color="#9B5DE5" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </motion.div>

        {/* Certificate Print Modal */}
        {showCertModal && (
          <CertificatePrintModal
            certificate={showCertModal}
            childName={child?.name}
            onClose={() => setShowCertModal(null)}
          />
        )}

        {/* Progress totals */}
        <motion.div 
          className="bg-white rounded-3xl p-5 shadow-xl mb-5 border-b-4" 
          style={{ borderColor: "#FFD166" }}
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.6 }}
        >
          <h3 className="font-black text-[#073B4C] mb-4" style={{ fontFamily: "Fredoka, sans-serif" }}>
            {t('total_progress')}
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <motion.div 
              className="rounded-2xl p-4 text-center border-b-4"
              style={{ background: "linear-gradient(135deg, #E0F4FF, #118AB2)", borderColor: "#0d6e91" }}
              whileHover={{ scale: 1.05, y: -5 }}
              transition={{ type: "spring" }}
            >
              <motion.p 
                className="text-3xl font-black text-white"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", delay: 0.8 }}
              >
                {progress?.total_lessons||0}
              </motion.p>
              <p className="text-white/80 text-xs font-bold mt-1">{t('lessons_done')}</p>
            </motion.div>
            <motion.div 
              className="rounded-2xl p-4 text-center border-b-4"
              style={{ background: "linear-gradient(135deg, #F0E6FF, #9B5DE5)", borderColor: "#7a3fc3" }}
              whileHover={{ scale: 1.05, y: -5 }}
              transition={{ type: "spring" }}
            >
              <motion.p 
                className="text-3xl font-black text-white"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", delay: 0.9 }}
              >
                {progress?.total_tasks||0}
              </motion.p>
              <p className="text-white/80 text-xs font-bold mt-1">{t('activities_done')}</p>
            </motion.div>
          </div>
        </motion.div>

        {/* 7-day calendar */}
        {progress?.recent_progress?.length > 0 && (
          <motion.div 
            className="bg-white rounded-3xl p-5 shadow-sm mb-5 border-b-4" 
            style={{ borderColor: "#06D6A0" }}
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.7 }}
          >
            <h3 className="font-black text-[#073B4C] mb-4" style={{ fontFamily: "Fredoka, sans-serif" }}>
              {t('last_7_days')}
            </h3>
            <div className="flex gap-2 justify-center flex-wrap">
              {progress.recent_progress.slice(0,7).map((day,i) => (
                <motion.div 
                  key={i} 
                  className="text-center"
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.8 + i * 0.1, type: "spring" }}
                >
                  <motion.div 
                    className="w-11 h-11 rounded-full flex items-center justify-center border-2 transition-all"
                    style={day.lesson_completed
                      ? { background: "linear-gradient(135deg, #06D6A0, #118AB2)", borderColor: "#06D6A0" }
                      : { background: "#F3F4F6", borderColor: "#E5E7EB" }}
                    data-testid={`day-progress-${i}`}
                    whileHover={{ scale: 1.2 }}
                    animate={day.lesson_completed ? { 
                      boxShadow: ["0 0 0px rgba(6,214,160,0.5)", "0 0 15px rgba(6,214,160,0.5)", "0 0 0px rgba(6,214,160,0.5)"]
                    } : {}}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    {day.lesson_completed
                      ? <BookOpen size={16} color="white" strokeWidth={2.5} />
                      : <span className="text-gray-400 text-xs font-bold">{i+1}</span>}
                  </motion.div>
                  <p className="text-xs text-gray-400 mt-1 font-semibold">
                    {new Date(day.date).toLocaleDateString(language === 'es' ? "es-ES" : "en-US",{weekday:"short"})[0]}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Badges grid */}
        <motion.h3 
          className="text-xl font-black text-[#073B4C] mb-4" 
          style={{ fontFamily: "Fredoka, sans-serif" }}
          initial={{ x: -20, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.9 }}
        >
          {t('badges_collection')}
        </motion.h3>
        <div className="grid grid-cols-2 gap-4 mb-4">
          {ALL_BADGES.map((badge, index) => {
            const earned = earnedTypes.has(badge.type);
            const cfg = BADGE_CONFIG[badge.type];
            const Icon = cfg.icon;
            return (
              <motion.div 
                key={badge.type}
                className={`rounded-3xl p-5 text-center shadow-sm border-b-4 relative overflow-hidden ${earned ? "" : "opacity-40"}`}
                style={earned
                  ? { background: `linear-gradient(135deg, ${cfg.grad[0]}22, ${cfg.grad[1]}22)`,
                      borderColor: cfg.grad[0] }
                  : { background: "#F9FAFB", borderColor: "#E5E7EB" }}
                data-testid={`badge-${badge.type}`}
                initial={{ y: 30, opacity: 0, scale: 0.9 }}
                animate={{ y: 0, opacity: earned ? 1 : 0.4, scale: 1 }}
                transition={{ delay: 1 + index * 0.1, type: "spring" }}
                whileHover={earned ? { scale: 1.05, y: -5 } : {}}
                onClick={() => earned && setCelebrateBadge(badge.type)}
              >
                {/* Celebration effect for earned badges */}
                {earned && celebrateBadge === badge.type && (
                  <SparkleBurst show={true} />
                )}
                
                <motion.div 
                  className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3 shadow relative"
                  style={{ background: earned
                    ? `linear-gradient(135deg, ${cfg.grad[0]}, ${cfg.grad[1]})`
                    : "#E5E7EB" }}
                  animate={earned ? { 
                    rotate: [0, 5, -5, 0],
                    scale: [1, 1.05, 1],
                  } : {}}
                  transition={{ duration: 3, repeat: Infinity, repeatDelay: 2 }}
                >
                  <Icon size={30} color={earned ? "white" : "#9CA3AF"} strokeWidth={2.5} />
                  
                  {/* Glowing ring for earned badges */}
                  {earned && (
                    <motion.div
                      className="absolute inset-0 rounded-full"
                      style={{ border: `3px solid ${cfg.grad[0]}` }}
                      animate={{ 
                        scale: [1, 1.3, 1],
                        opacity: [0.8, 0, 0.8],
                      }}
                      transition={{ duration: 2, repeat: Infinity }}
                    />
                  )}
                </motion.div>
                <p className={`font-black text-sm mb-1 ${earned ? "text-[#073B4C]" : "text-gray-400"}`}
                  style={{ fontFamily: "Fredoka, sans-serif" }}>{badge.name}</p>
                <p className={`text-xs leading-tight ${earned ? "text-gray-500" : "text-gray-300"}`}>
                  {badge.description}
                </p>
                {earned && (
                  <motion.div 
                    className="mt-2 flex items-center justify-center gap-2"
                    initial={{ y: 10, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 1.2 + index * 0.1 }}
                  >
                    <motion.span 
                      className="text-white text-xs font-black px-3 py-0.5 rounded-full"
                      style={{ background: cfg.grad[0] }}
                      whileHover={{ scale: 1.1 }}
                    >
                      {t('earned')}
                    </motion.span>
                    <QuickShareButton
                      type="badge"
                      data={{
                        childName: child?.name,
                        badgeName: badge.name,
                      }}
                      className="hover:bg-white/50"
                    />
                  </motion.div>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
      <BottomNav childId={childId} active="rewards" />
    </div>
  );
}

function CertificatePrintModal({ certificate, childName, onClose }) {
  const handlePrint = () => {
    const win = window.open("", "_blank");
    win.document.write(`
      <html><head><title>Bible Buds Certificate</title>
      <style>
        body { margin: 0; display: flex; justify-content: center; align-items: center; min-height: 100vh; background: #f0f0f0; }
        .cert { width: 800px; padding: 60px; background: white; border: 8px solid #9B5DE5; border-radius: 20px; text-align: center; font-family: Georgia, serif; position: relative; }
        .cert::before { content: ''; position: absolute; inset: 8px; border: 3px solid #FFD166; border-radius: 14px; }
        h1 { color: #9B5DE5; font-size: 42px; margin-bottom: 8px; }
        h2 { color: #073B4C; font-size: 28px; margin: 24px 0 8px; }
        .name { color: #EF476F; font-size: 36px; font-weight: bold; margin: 16px 0; border-bottom: 3px solid #EF476F; display: inline-block; padding-bottom: 4px; }
        .theme { color: #118AB2; font-size: 20px; margin: 12px 0; }
        .scripture { color: #666; font-style: italic; font-size: 16px; margin: 8px 0 24px; }
        .stars { font-size: 32px; margin: 16px 0; }
        .date { color: #999; font-size: 14px; margin-top: 32px; }
        .present { color: #9B5DE5; font-size: 16px; margin-top: 24px; font-weight: bold; }
        @media print { body { background: white; } .cert { border-width: 4px; } }
      </style></head><body>
      <div class="cert">
        <div class="stars">&#9733; &#9733; &#9733; &#9733; &#9733;</div>
        <h1>Bible Buds</h1>
        <h2>Certificate of Completion</h2>
        <p style="color:#666;font-size:18px;">This certifies that</p>
        <div class="name">${childName || certificate.child_name}</div>
        <p style="color:#666;font-size:18px;">has successfully completed all lessons and activities for</p>
        <div class="theme">Week ${certificate.week_number}: ${certificate.theme}</div>
        <div class="scripture">${certificate.scripture}</div>
        <div class="stars">&#9733; &#9733; &#9733; &#9733; &#9733;</div>
        <div class="present">Present this certificate at Family Lesson!</div>
        <div class="date">${new Date(certificate.earned_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</div>
      </div>
      <script>window.print();</script>
      </body></html>
    `);
    win.document.close();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="p-4 flex items-center justify-between" style={{ background: "linear-gradient(135deg, #9B5DE5, #EF476F)" }}>
          <div className="flex items-center gap-2">
            <Award size={24} color="white" />
            <h2 className="text-white font-black text-lg" style={{ fontFamily: "Fredoka, sans-serif" }}>
              Weekly Certificate
            </h2>
          </div>
          <button onClick={onClose} className="bg-white/20 hover:bg-white/30 rounded-xl p-2 transition-all">
            <span className="text-white font-bold text-lg">&times;</span>
          </button>
        </div>
        <div className="p-6">
          <div className="border-4 border-[#9B5DE5] rounded-2xl p-6 text-center relative">
            <div className="absolute inset-2 border-2 border-[#FFD166] rounded-xl pointer-events-none" />
            <div className="flex justify-center gap-1 mb-3">
              {[...Array(5)].map((_, i) => <Star key={i} size={24} color="#FFD166" fill="#FFD166" />)}
            </div>
            <h3 className="text-2xl font-black text-[#9B5DE5]" style={{ fontFamily: "Fredoka, sans-serif" }}>Bible Buds</h3>
            <p className="text-lg font-bold text-[#073B4C] mt-3">Certificate of Completion</p>
            <p className="text-sm text-gray-500 mt-2">This certifies that</p>
            <p className="text-2xl font-black text-[#EF476F] mt-1 border-b-2 border-[#EF476F] inline-block pb-1"
              style={{ fontFamily: "Fredoka, sans-serif" }}>
              {childName || certificate.child_name}
            </p>
            <p className="text-sm text-gray-500 mt-3">has completed all lessons & activities for</p>
            <p className="text-lg font-bold text-[#118AB2] mt-1">Week {certificate.week_number}: {certificate.theme}</p>
            <p className="text-sm text-gray-400 italic mt-1">{certificate.scripture}</p>
            <div className="flex justify-center gap-1 mt-4">
              {[...Array(5)].map((_, i) => <Star key={i} size={20} color="#FFD166" fill="#FFD166" />)}
            </div>
            <p className="text-sm font-bold text-[#9B5DE5] mt-3">Present this at Family Lesson!</p>
            <p className="text-xs text-gray-400 mt-2">
              {new Date(certificate.earned_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
            </p>
          </div>
        </div>
        <div className="px-6 pb-6">
          <button onClick={handlePrint}
            className="w-full py-3 rounded-xl font-bold text-white flex items-center justify-center gap-2 hover:scale-[1.02] active:scale-[0.98] transition-all"
            style={{ background: "linear-gradient(90deg, #9B5DE5, #EF476F)" }}
            data-testid="print-cert-modal-btn"
          >
            <Download size={18} />
            Print Certificate
          </button>
        </div>
      </div>
    </div>
  );
}
